import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, GripVertical, Plus, Edit3, Trash2, X } from 'lucide-react';
import { useTenantContext } from '../../context/TenantContext';
import { IconSelector } from './IconSelector';
import { getServiceFiles } from '../../utils/autoServiceScanner';

interface PublicHeaderConfigProps {
  formData: any;
  handleConfigChange: (path: string, value: any) => void;
}

// Les icônes sont maintenant gérées par le composant IconSelector centralisé
// Voir src/components/admin/IconSelector.tsx pour modifier la liste

const defaultPublicMenuItems = [
  { id: 'home', name: 'Accueil', enabled: true, icon: 'Home', href: '/', isCustom: false },
  { 
    id: 'services', 
    name: 'Nos Services', 
    enabled: true, 
    icon: 'Wrench', 
    href: '/services', 
    isCustom: false,
    submenu: [] // Sera rempli automatiquement
  },
  { id: 'boutique', name: 'Boutique', enabled: true, icon: 'ShoppingCart', href: '/boutique', isCustom: false },
  { id: 'about', name: 'À Propos', enabled: true, icon: 'Info', href: '/about', isCustom: false },
];

const PublicHeaderConfig: React.FC<PublicHeaderConfigProps> = ({ formData, handleConfigChange }) => {
  const { setPreviewValue } = useTenantContext();

  // Synchroniser automatiquement les sous-menus Services
  useEffect(() => {
    const servicesFolder = formData.config?.services?.folder;
    const servicesRoutes = formData.config?.services?.routes || {};
    
    if (servicesFolder) {
      const serviceFiles = getServiceFiles(servicesFolder);
      
      // Créer les sous-menus automatiquement
      const autoSubmenu = serviceFiles.map(file => ({
        id: `service_${file.key}`,
        name: file.label,
        icon: 'Wrench',
        href: `/services/${servicesRoutes[file.key] || file.defaultRoute}`,
        enabled: true,
        isCustom: false,
        isAuto: true // Marqueur pour identifier les sous-menus auto
      }));

      // Mettre à jour le menu Services avec les sous-menus automatiques
      const currentMenuItems = formData.config.publicHeader?.menuItems || defaultPublicMenuItems;
      const servicesMenuItem = currentMenuItems.find((item: any) => item.id === 'services');
      
      if (servicesMenuItem) {
        // Garder les sous-menus personnalisés (isCustom: true) et ajouter les auto
        const customSubmenus = servicesMenuItem.submenu?.filter((sub: any) => sub.isCustom) || [];
        const updatedSubmenu = [...autoSubmenu, ...customSubmenus];
        
        // Vérifier si les sous-menus ont changé
        const currentSubmenuJson = JSON.stringify(servicesMenuItem.submenu || []);
        const newSubmenuJson = JSON.stringify(updatedSubmenu);
        
        if (currentSubmenuJson !== newSubmenuJson) {
          const updatedMenuItems = currentMenuItems.map((item: any) => 
            item.id === 'services' 
              ? { ...item, submenu: updatedSubmenu }
              : item
          );
          
          handleConfigChange('publicHeader.menuItems', updatedMenuItems);
          console.log('🔄 Sous-menus Services mis à jour automatiquement:', updatedSubmenu);
        }
      }
    }
  }, [formData.config?.services?.folder, formData.config?.services?.routes]);

  // Couleurs Header Public
  const handlePublicHeaderColorChange = (key: 'textColor' | 'backgroundColor' | 'iconColor', value: string) => {
    handleConfigChange(`publicHeader.${key}`, value);
    setPreviewValue(`publicHeader.${key}`, value);
  };

  // Default/merge topbar
  const defaultTopbar = {
    enabled: true,
    showOnMobile: false,
    size: 'compact',
    backgroundColor: formData.config.publicHeader?.backgroundColor || '#0f172a',
    textColor: formData.config.publicHeader?.textColor || '#ffffff',
    iconColor: formData.config.publicHeader?.iconColor || '#f97316',
    borderBottom: true,
    borderColor: '#e5e7eb',
    left: {
      showPhone: false,
      phone: '',
      showEmail: false,
      email: '',
      showHours: false,
      hours: 'Lun-Ven: 9h-18h',
    },
    message: {
      enabled: false,
      text: '',
      icon: 'Info',
      href: '',
      dismissible: true,
    },
    social: {
      enabled: false,
      items: [
        { id: 'facebook', name: 'Facebook', icon: 'Facebook', url: '', enabled: false },
        { id: 'twitter', name: 'Twitter', icon: 'Twitter', url: '', enabled: false },
        { id: 'instagram', name: 'Instagram', icon: 'Instagram', url: '', enabled: false },
        { id: 'linkedin', name: 'LinkedIn', icon: 'Linkedin', url: '', enabled: false },
        { id: 'youtube', name: 'YouTube', icon: 'Youtube', url: '', enabled: false },
      ],
    },
  };

  const topbar = {
    ...defaultTopbar,
    ...formData.config.publicHeader?.topbar,
    left: {
      ...defaultTopbar.left,
      ...formData.config.publicHeader?.topbar?.left,
    },
    message: {
      ...defaultTopbar.message,
      ...formData.config.publicHeader?.topbar?.message,
    },
    social: {
      ...defaultTopbar.social,
      ...formData.config.publicHeader?.topbar?.social,
      items: formData.config.publicHeader?.topbar?.social?.items || defaultTopbar.social.items,
    },
  };

  const publicMenuItems = formData.config.publicHeader?.menuItems || defaultPublicMenuItems;
  const publicCTA = formData.config.publicHeader?.cta || { label: 'Contact', href: '/contact', enabled: true };

  // Menus publics - états locaux
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<{[key: string]: {name: string, href: string, icon: string}}>({});
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);
  const [newPublicMenuItem, setNewPublicMenuItem] = useState({ name: '', icon: 'Home', href: '', enabled: true });

  const [editingSubmenu, setEditingSubmenu] = useState<string | null>(null);
  const [newSubMenuItem, setNewSubMenuItem] = useState({ name: '', icon: 'Home', href: '', enabled: true });

  const handlePublicMenuItemToggle = (itemId: string) => {
    const updatedItems = publicMenuItems.map((item: any) => item.id === itemId ? { ...item, enabled: !item.enabled } : item);
    handleConfigChange('publicHeader.menuItems', updatedItems);
    setPreviewValue('publicHeader.menuItems', updatedItems);
  };

  const handlePublicDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItem(itemId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent, itemId: string) => { e.preventDefault(); setDragOverItem(itemId); };
  const handleDragLeave = () => setDragOverItem(null);
  const handlePublicDrop = (e: React.DragEvent, targetItemId: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetItemId) return;
    const draggedIndex = publicMenuItems.findIndex((i: any) => i.id === draggedItem);
    const targetIndex = publicMenuItems.findIndex((i: any) => i.id === targetItemId);
    if (draggedIndex === -1 || targetIndex === -1) return;
    const newItems = [...publicMenuItems];
    const [dragged] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, dragged);
    handleConfigChange('publicHeader.menuItems', [...newItems]);
    setPreviewValue('publicHeader.menuItems', [...newItems]);
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handlePublicAddMenuItem = () => {
    if (!newPublicMenuItem.name.trim()) return;
    const newId = `pub_${Date.now()}`;
    const newItem = { id: newId, name: newPublicMenuItem.name.trim(), icon: newPublicMenuItem.icon, href: newPublicMenuItem.href || `/${newId}`, enabled: newPublicMenuItem.enabled, isCustom: true };
    const updatedItems = [...publicMenuItems, newItem];
    handleConfigChange('publicHeader.menuItems', updatedItems);
    setPreviewValue('publicHeader.menuItems', updatedItems);
    setNewPublicMenuItem({ name: '', icon: 'Home', href: '', enabled: true });
    setShowAddForm(false);
  };

  const handlePublicDeleteMenuItem = (itemId: string) => {
    const updatedItems = publicMenuItems.filter((item: any) => item.id !== itemId);
    handleConfigChange('publicHeader.menuItems', updatedItems);
    setPreviewValue('publicHeader.menuItems', updatedItems);
  };

  const handlePublicEditMenuItem = (itemId: string, updates: any) => {
    const updatedItems = publicMenuItems.map((item: any) => item.id === itemId ? { ...item, ...updates } : item);
    handleConfigChange('publicHeader.menuItems', updatedItems);
    setPreviewValue('publicHeader.menuItems', updatedItems);
    setEditingItem(null);
  };

  const handleAddSubmenuItem = (parentId: string) => {
    if (!newSubMenuItem.name.trim()) return;
    const newSubId = `sub_${Date.now()}`;
    const newSubItem = { id: newSubId, name: newSubMenuItem.name.trim(), icon: newSubMenuItem.icon, href: newSubMenuItem.href || `/${newSubId}`, enabled: newSubMenuItem.enabled, isCustom: true };
    const updatedItems = publicMenuItems.map((item: any) => item.id === parentId ? { ...item, submenu: [...(item.submenu || []), newSubItem] } : item);
    handleConfigChange('publicHeader.menuItems', updatedItems);
    setPreviewValue('publicHeader.menuItems', updatedItems);
    setNewSubMenuItem({ name: '', icon: 'Home', href: '', enabled: true });
    setEditingSubmenu(null);
  };

  const handleDeleteSubmenuItem = (parentId: string, subItemId: string) => {
    const updatedItems = publicMenuItems.map((item: any) => (item.id === parentId && item.submenu) ? { ...item, submenu: item.submenu.filter((s: any) => s.id !== subItemId) } : item);
    handleConfigChange('publicHeader.menuItems', updatedItems);
    setPreviewValue('publicHeader.menuItems', updatedItems);
  };

  const handleEditSubmenuItem = (parentId: string, subItemId: string, updates: any) => {
    const updatedItems = publicMenuItems.map((item: any) => (item.id === parentId && item.submenu) ? { ...item, submenu: item.submenu.map((s: any) => s.id === subItemId ? { ...s, ...updates } : s) } : item);
    handleConfigChange('publicHeader.menuItems', updatedItems);
    setPreviewValue('publicHeader.menuItems', updatedItems);
  };

  return (
    <div className="space-y-8">
      {/* Section Couleurs du Header Public */}
      <div>
        <h3 className="text-lg font-medium text-white mb-6 border-b border-white/10 pb-2">Couleurs du header public</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur du texte
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.textColor)</span>
            </label>
            <div className="flex space-x-2">
              <input type="color" value={formData.config.publicHeader?.textColor || '#1e1b4b'} onChange={(e) => handlePublicHeaderColorChange('textColor', e.target.value)} className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
              <input type="text" value={formData.config.publicHeader?.textColor || '#1e1b4b'} onChange={(e) => handlePublicHeaderColorChange('textColor', e.target.value)} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur du background
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.backgroundColor)</span>
            </label>
            <div className="flex space-x-2">
              <input type="color" value={formData.config.publicHeader?.backgroundColor || '#ffffff'} onChange={(e) => handlePublicHeaderColorChange('backgroundColor', e.target.value)} className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
              <input type="text" value={formData.config.publicHeader?.backgroundColor || '#ffffff'} onChange={(e) => handlePublicHeaderColorChange('backgroundColor', e.target.value)} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur des icônes
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.iconColor)</span>
            </label>
            <div className="flex space-x-2">
              <input type="color" value={formData.config.publicHeader?.iconColor || '#f97316'} onChange={(e) => handlePublicHeaderColorChange('iconColor', e.target.value)} className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
              <input type="text" value={formData.config.publicHeader?.iconColor || '#f97316'} onChange={(e) => handlePublicHeaderColorChange('iconColor', e.target.value)} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors" />
            </div>
          </div>
        </div>
      </div>


      {/* Section Topbar */}
      <div>
        <h3 className="text-lg font-medium text-white mb-6 border-b border-white/10 pb-2">Topbar</h3>
        <div className="space-y-6">
          {/* Activation & affichage */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center space-x-3">
              <input type="checkbox" id="topbar-enabled" checked={formData.config.publicHeader?.topbar?.enabled ?? topbar.enabled} onChange={(e) => { handleConfigChange('publicHeader.topbar.enabled', e.target.checked); setPreviewValue('publicHeader.topbar.enabled', e.target.checked); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
              <label htmlFor="topbar-enabled" className="text-gray-200 cursor-pointer">Activer la topbar</label>
            </div>
            <div className="flex items-center space-x-3">
              <input type="checkbox" id="topbar-mobile" checked={formData.config.publicHeader?.topbar?.showOnMobile ?? topbar.showOnMobile} onChange={(e) => { handleConfigChange('publicHeader.topbar.showOnMobile', e.target.checked); setPreviewValue('publicHeader.topbar.showOnMobile', e.target.checked); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
              <label htmlFor="topbar-mobile" className="text-gray-200 cursor-pointer">Afficher sur mobile</label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Taille</label>
              <select value={formData.config.publicHeader?.topbar?.size || topbar.size} onChange={(e) => { handleConfigChange('publicHeader.topbar.size', e.target.value); setPreviewValue('publicHeader.topbar.size', e.target.value); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" style={{ colorScheme: 'dark' }}>
                <option value="compact" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Compacte</option>
                <option value="normal" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Normale</option>
                <option value="large" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Grande</option>
              </select>
            </div>
          </div>

          {/* Couleurs & bordure */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Couleur de fond</label>
              <div className="flex space-x-2">
                <input type="color" value={formData.config.publicHeader?.topbar?.backgroundColor || topbar.backgroundColor} onChange={(e) => { handleConfigChange('publicHeader.topbar.backgroundColor', e.target.value); setPreviewValue('publicHeader.topbar.backgroundColor', e.target.value); }} className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text" value={formData.config.publicHeader?.topbar?.backgroundColor || topbar.backgroundColor} onChange={(e) => { handleConfigChange('publicHeader.topbar.backgroundColor', e.target.value); setPreviewValue('publicHeader.topbar.backgroundColor', e.target.value); }} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Couleur du texte</label>
              <div className="flex space-x-2">
                <input type="color" value={formData.config.publicHeader?.topbar?.textColor || topbar.textColor} onChange={(e) => { handleConfigChange('publicHeader.topbar.textColor', e.target.value); setPreviewValue('publicHeader.topbar.textColor', e.target.value); }} className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text" value={formData.config.publicHeader?.topbar?.textColor || topbar.textColor} onChange={(e) => { handleConfigChange('publicHeader.topbar.textColor', e.target.value); setPreviewValue('publicHeader.topbar.textColor', e.target.value); }} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Couleur des icônes</label>
              <div className="flex space-x-2">
                <input type="color" value={formData.config.publicHeader?.topbar?.iconColor || topbar.iconColor} onChange={(e) => { handleConfigChange('publicHeader.topbar.iconColor', e.target.value); setPreviewValue('publicHeader.topbar.iconColor', e.target.value); }} className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text" value={formData.config.publicHeader?.topbar?.iconColor || topbar.iconColor} onChange={(e) => { handleConfigChange('publicHeader.topbar.iconColor', e.target.value); setPreviewValue('publicHeader.topbar.iconColor', e.target.value); }} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Bordure basse</label>
              <div className="flex items-center space-x-3 mb-2">
                <input type="checkbox" id="topbar-border" checked={formData.config.publicHeader?.topbar?.borderBottom ?? topbar.borderBottom} onChange={(e) => { handleConfigChange('publicHeader.topbar.borderBottom', e.target.checked); setPreviewValue('publicHeader.topbar.borderBottom', e.target.checked); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
                <label htmlFor="topbar-border" className="text-gray-200 cursor-pointer">Afficher la bordure</label>
              </div>
              <div className="flex space-x-2">
                <input type="color" value={formData.config.publicHeader?.topbar?.borderColor || topbar.borderColor} onChange={(e) => { handleConfigChange('publicHeader.topbar.borderColor', e.target.value); setPreviewValue('publicHeader.topbar.borderColor', e.target.value); }} className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text" value={formData.config.publicHeader?.topbar?.borderColor || topbar.borderColor} onChange={(e) => { handleConfigChange('publicHeader.topbar.borderColor', e.target.value); setPreviewValue('publicHeader.topbar.borderColor', e.target.value); }} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" />
              </div>
            </div>
          </div>

          {/* Contenu gauche: Contact */}
          <div className="bg-white/5 rounded-lg p-6 border border-white/10">
            <h4 className="text-md font-semibold text-white mb-4">Contenu gauche (contact)</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.config.publicHeader?.topbar?.left?.showPhone ?? topbar.left.showPhone} onChange={(e) => { handleConfigChange('publicHeader.topbar.left.showPhone', e.target.checked); setPreviewValue('publicHeader.topbar.left.showPhone', e.target.checked); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
                  <span className="text-gray-200 text-sm">Afficher le téléphone</span>
                </label>
                <input type="text" placeholder="Ex: +237 6 99 00 00 00" value={formData.config.publicHeader?.topbar?.left?.phone ?? topbar.left.phone} onChange={(e) => { handleConfigChange('publicHeader.topbar.left.phone', e.target.value); setPreviewValue('publicHeader.topbar.left.phone', e.target.value); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" />
              </div>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.config.publicHeader?.topbar?.left?.showEmail ?? topbar.left.showEmail} onChange={(e) => { handleConfigChange('publicHeader.topbar.left.showEmail', e.target.checked); setPreviewValue('publicHeader.topbar.left.showEmail', e.target.checked); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
                  <span className="text-gray-200 text-sm">Afficher l'email</span>
                </label>
                <input type="email" placeholder="Ex: contact@votre-domaine.com" value={formData.config.publicHeader?.topbar?.left?.email ?? topbar.left.email} onChange={(e) => { handleConfigChange('publicHeader.topbar.left.email', e.target.value); setPreviewValue('publicHeader.topbar.left.email', e.target.value); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" />
              </div>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.config.publicHeader?.topbar?.left?.showHours ?? topbar.left.showHours} onChange={(e) => { handleConfigChange('publicHeader.topbar.left.showHours', e.target.checked); setPreviewValue('publicHeader.topbar.left.showHours', e.target.checked); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
                  <span className="text-gray-200 text-sm">Afficher les horaires</span>
                </label>
                <input type="text" placeholder="Ex: Lun-Ven 9h-18h" value={formData.config.publicHeader?.topbar?.left?.hours ?? topbar.left.hours} onChange={(e) => { handleConfigChange('publicHeader.topbar.left.hours', e.target.value); setPreviewValue('publicHeader.topbar.left.hours', e.target.value); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" />
              </div>
            </div>
          </div>

          {/* Message/Annonce */}
          <div className="bg-white/5 rounded-lg p-6 border border-white/10">
            <h4 className="text-md font-semibold text-white mb-4">Message / Annonce</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="flex items-center space-x-3">
                <input type="checkbox" id="topbar-message-enabled" checked={formData.config.publicHeader?.topbar?.message?.enabled ?? topbar.message.enabled} onChange={(e) => { handleConfigChange('publicHeader.topbar.message.enabled', e.target.checked); setPreviewValue('publicHeader.topbar.message.enabled', e.target.checked); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
                <label htmlFor="topbar-message-enabled" className="text-gray-200 cursor-pointer">Activer le message</label>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Texte du message</label>
                <input type="text" placeholder="Ex: Livraison offerte dès 50 000 FCFA" value={formData.config.publicHeader?.topbar?.message?.text ?? topbar.message.text} onChange={(e) => { handleConfigChange('publicHeader.topbar.message.text', e.target.value); setPreviewValue('publicHeader.topbar.message.text', e.target.value); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Icône</label>
                <IconSelector
                  value={formData.config.publicHeader?.topbar?.message?.icon ?? topbar.message.icon}
                  onChange={(value) => {
                    handleConfigChange('publicHeader.topbar.message.icon', value);
                    setPreviewValue('publicHeader.topbar.message.icon', value);
                  }}
                  preset="all"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-300 mb-2">Lien (optionnel)</label>
                <input type="text" placeholder="Ex: /promotions" value={formData.config.publicHeader?.topbar?.message?.href ?? topbar.message.href} onChange={(e) => { handleConfigChange('publicHeader.topbar.message.href', e.target.value); setPreviewValue('publicHeader.topbar.message.href', e.target.value); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" />
              </div>
              <div className="flex items-center space-x-3">
                <input type="checkbox" id="topbar-message-dismiss" checked={formData.config.publicHeader?.topbar?.message?.dismissible ?? topbar.message.dismissible} onChange={(e) => { handleConfigChange('publicHeader.topbar.message.dismissible', e.target.checked); setPreviewValue('publicHeader.topbar.message.dismissible', e.target.checked); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
                <label htmlFor="topbar-message-dismiss" className="text-gray-200 cursor-pointer">Message masquable</label>
              </div>
            </div>
          </div>

          {/* Réseaux sociaux */}
          <div className="bg-white/5 rounded-lg p-6 border border-white/10">
            <h4 className="text-md font-semibold text-white mb-4">Réseaux sociaux</h4>
            <div className="flex items-center space-x-3 mb-4">
              <input type="checkbox" id="topbar-social-enabled" checked={formData.config.publicHeader?.topbar?.social?.enabled ?? topbar.social.enabled} onChange={(e) => { handleConfigChange('publicHeader.topbar.social.enabled', e.target.checked); setPreviewValue('publicHeader.topbar.social.enabled', e.target.checked); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
              <label htmlFor="topbar-social-enabled" className="text-gray-200 cursor-pointer">Afficher les icônes sociales</label>
            </div>
            {(() => {
              const defaults = topbar.social.items;
              const socialItems = (formData.config.publicHeader?.topbar?.social?.items && Array.isArray(formData.config.publicHeader.topbar.social.items)) ? formData.config.publicHeader.topbar.social.items : defaults;
              return (
                <div className="space-y-3">
                  {socialItems.map((s: any, idx: number) => (
                    <div key={s.id || idx} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                      <div className="md:col-span-3 flex items-center space-x-2">
                        <span className="text-sm text-gray-300 font-medium">{s.name || s.id}</span>
                        <span className="text-xs text-gray-500">({s.icon})</span>
                      </div>
                      <div className="md:col-span-2 flex items-center space-x-2">
                        <input type="checkbox" checked={!!s.enabled} onChange={(e) => { const newItems = [...socialItems]; newItems[idx] = { ...s, enabled: e.target.checked }; handleConfigChange('publicHeader.topbar.social.items', newItems); setPreviewValue('publicHeader.topbar.social.items', newItems); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
                        <span className="text-gray-200 text-sm">Activer</span>
                      </div>
                      <div className="md:col-span-7">
                        <input type="text" placeholder={`URL ${s.name || s.id}`} value={s.url || ''} onChange={(e) => { const newItems = [...socialItems]; newItems[idx] = { ...s, url: e.target.value }; handleConfigChange('publicHeader.topbar.social.items', newItems); setPreviewValue('publicHeader.topbar.social.items', newItems); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" />
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Section Logo */}
      <div>
        <h3 className="text-lg font-medium text-white mb-6 border-b border-white/10 pb-2">Configuration du logo</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Position du logo
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.logo.position)</span>
            </label>
            <select value={formData.config.publicHeader?.logo?.position || 'center'} onChange={(e) => { handleConfigChange('publicHeader.logo.position', e.target.value); setPreviewValue('publicHeader.logo.position', e.target.value); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" style={{ colorScheme: 'dark' }}>
              <option value="left" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Gauche</option>
              <option value="center" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Centre</option>
              <option value="right" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Droite</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Taille du logo
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.logo.size)</span>
            </label>
            <select value={formData.config.publicHeader?.logo?.size || 'medium'} onChange={(e) => { handleConfigChange('publicHeader.logo.size', e.target.value); setPreviewValue('publicHeader.logo.size', e.target.value); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" style={{ colorScheme: 'dark' }}>
              <option value="small" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Petit (h-8)</option>
              <option value="medium" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Moyen (h-12)</option>
              <option value="large" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Grand (h-16)</option>
              <option value="extra-large" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Très grand (h-20)</option>
              <option value="ultra-large" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Ultra grand (h-24)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Forme du conteneur
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.logo.shape)</span>
            </label>
            <select value={formData.config.publicHeader?.logo?.shape || 'rounded-xl'} onChange={(e) => { handleConfigChange('publicHeader.logo.shape', e.target.value); setPreviewValue('publicHeader.logo.shape', e.target.value); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" style={{ colorScheme: 'dark' }}>
              <option value="rounded-none" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Carré</option>
              <option value="rounded-md" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Coins arrondis</option>
              <option value="rounded-xl" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Très arrondi</option>
              <option value="rounded-full" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Cercle</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur de fond du logo
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.logo.backgroundColor)</span>
            </label>
            <div className="flex space-x-2">
              <input type="color" value={formData.config.publicHeader?.logo?.backgroundColor || '#ffffff'} onChange={(e) => { handleConfigChange('publicHeader.logo.backgroundColor', e.target.value); setPreviewValue('publicHeader.logo.backgroundColor', e.target.value); }} className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
              <input type="text" value={formData.config.publicHeader?.logo?.backgroundColor || '#ffffff'} onChange={(e) => { handleConfigChange('publicHeader.logo.backgroundColor', e.target.value); setPreviewValue('publicHeader.logo.backgroundColor', e.target.value); }} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur de bordure
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.logo.borderColor)</span>
            </label>
            <div className="flex space-x-2">
              <input type="color" value={formData.config.publicHeader?.logo?.borderColor || '#e5e7eb'} onChange={(e) => { handleConfigChange('publicHeader.logo.borderColor', e.target.value); setPreviewValue('publicHeader.logo.borderColor', e.target.value); }} className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
              <input type="text" value={formData.config.publicHeader?.logo?.borderColor || '#e5e7eb'} onChange={(e) => { handleConfigChange('publicHeader.logo.borderColor', e.target.value); setPreviewValue('publicHeader.logo.borderColor', e.target.value); }} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Padding du contenu
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.logo.padding)</span>
            </label>
            <select value={formData.config.publicHeader?.logo?.padding || 'p-2'} onChange={(e) => { handleConfigChange('publicHeader.logo.padding', e.target.value); setPreviewValue('publicHeader.logo.padding', e.target.value); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" style={{ colorScheme: 'dark' }}>
              <option value="p-0" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Aucun (0)</option>
              <option value="p-1" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Très petit (1)</option>
              <option value="p-2" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Petit (2)</option>
              <option value="p-3" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Normal (3)</option>
              <option value="p-4" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Grand (4)</option>
              <option value="p-6" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Très grand (6)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Afficher la bordure
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.logo.showBorder)</span>
            </label>
            <div className="flex items-center space-x-3 mt-3">
              <input type="checkbox" id="logo-show-border" checked={!!formData.config.publicHeader?.logo?.showBorder} onChange={(e) => { handleConfigChange('publicHeader.logo.showBorder', e.target.checked); setPreviewValue('publicHeader.logo.showBorder', e.target.checked); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
              <label htmlFor="logo-show-border" className="text-gray-200 cursor-pointer">Afficher la bordure</label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Décalage vertical Mobile (px)
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.logo.verticalOffsetMobile)</span>
            </label>
            <input 
              type="number" 
              min="-50" 
              max="50" 
              value={formData.config.publicHeader?.logo?.verticalOffsetMobile || 0} 
              onChange={(e) => { 
                const value = parseInt(e.target.value) || 0; 
                handleConfigChange('publicHeader.logo.verticalOffsetMobile', value); 
                setPreviewValue('publicHeader.logo.verticalOffsetMobile', value); 
              }} 
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
              placeholder="0"
            />
            <div className="text-xs text-gray-500 mt-1">Négatif = monter, positif = descendre (mobile)</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Décalage vertical Desktop (px)
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.logo.verticalOffsetDesktop)</span>
            </label>
            <input 
              type="number" 
              min="-50" 
              max="50" 
              value={formData.config.publicHeader?.logo?.verticalOffsetDesktop || 0} 
              onChange={(e) => { 
                const value = parseInt(e.target.value) || 0; 
                handleConfigChange('publicHeader.logo.verticalOffsetDesktop', value); 
                setPreviewValue('publicHeader.logo.verticalOffsetDesktop', value); 
              }} 
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
              placeholder="0"
            />
            <div className="text-xs text-gray-500 mt-1">Négatif = monter, positif = descendre (desktop/lg)</div>
          </div>
        </div>
      </div>

      {/* Icônes décoratives */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
          <span className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full mr-3"></span>
          Icônes décoratives
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Afficher les icônes décoratives
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.decorativeIcons.enabled)</span>
            </label>
            <div className="flex items-center space-x-3 mt-3">
              <input type="checkbox" id="decorative-icons-enabled" checked={!!formData.config.publicHeader?.decorativeIcons?.enabled} onChange={(e) => { handleConfigChange('publicHeader.decorativeIcons.enabled', e.target.checked); setPreviewValue('publicHeader.decorativeIcons.enabled', e.target.checked); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
              <label htmlFor="decorative-icons-enabled" className="text-gray-200 cursor-pointer">Activer les icônes décoratives</label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Icône gauche
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.decorativeIcons.leftIcon)</span>
            </label>
            <IconSelector
              value={formData.config.publicHeader?.decorativeIcons?.leftIcon || 'Shield'}
              onChange={(value) => {
                handleConfigChange('publicHeader.decorativeIcons.leftIcon', value);
                setPreviewValue('publicHeader.decorativeIcons.leftIcon', value);
              }}
              preset="all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Icône droite
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.decorativeIcons.rightIcon)</span>
            </label>
            <IconSelector
              value={formData.config.publicHeader?.decorativeIcons?.rightIcon || 'Star'}
              onChange={(value) => {
                handleConfigChange('publicHeader.decorativeIcons.rightIcon', value);
                setPreviewValue('publicHeader.decorativeIcons.rightIcon', value);
              }}
              preset="all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur des icônes
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.decorativeIcons.color)</span>
            </label>
            <div className="flex items-center space-x-2">
              <input type="color" value={formData.config.publicHeader?.decorativeIcons?.color || '#f97316'} onChange={(e) => { handleConfigChange('publicHeader.decorativeIcons.color', e.target.value); setPreviewValue('publicHeader.decorativeIcons.color', e.target.value); }} className="w-12 h-8 rounded border border-white/20 bg-transparent cursor-pointer" />
              <input type="text" value={formData.config.publicHeader?.decorativeIcons?.color || '#f97316'} onChange={(e) => { handleConfigChange('publicHeader.decorativeIcons.color', e.target.value); setPreviewValue('publicHeader.decorativeIcons.color', e.target.value); }} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Typographie et tailles */}
      <div>
        <h3 className="text-lg font-medium text-white mb-6 border-b border-white/10 pb-2">Typographie et tailles</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Taille du texte
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.fontSize)</span>
            </label>
            <select value={formData.config.publicHeader?.fontSize || 'text-sm'} onChange={(e) => { handleConfigChange('publicHeader.fontSize', e.target.value); setPreviewValue('publicHeader.fontSize', e.target.value); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" style={{ colorScheme: 'dark' }}>
              <option value="text-xs" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Extra petit (xs)</option>
              <option value="text-sm" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Petit (sm)</option>
              <option value="text-base" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Normal (base)</option>
              <option value="text-lg" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Grand (lg)</option>
              <option value="text-xl" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Extra grand (xl)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Épaisseur du texte
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.fontWeight)</span>
            </label>
            <select value={formData.config.publicHeader?.fontWeight || 'font-bold'} onChange={(e) => { handleConfigChange('publicHeader.fontWeight', e.target.value); setPreviewValue('publicHeader.fontWeight', e.target.value); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" style={{ colorScheme: 'dark' }}>
              <option value="font-light" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Léger (light)</option>
              <option value="font-normal" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Normal</option>
              <option value="font-medium" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Moyen (medium)</option>
              <option value="font-semibold" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Semi-gras (semibold)</option>
              <option value="font-bold" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Gras (bold)</option>
              <option value="font-extrabold" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Très gras (extrabold)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Taille des icônes
              <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.iconSize)</span>
            </label>
            <select value={formData.config.publicHeader?.iconSize || 'h-5 w-5'} onChange={(e) => { handleConfigChange('publicHeader.iconSize', e.target.value); setPreviewValue('publicHeader.iconSize', e.target.value); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" style={{ colorScheme: 'dark' }}>
              <option value="h-4 w-4" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Petit (h-4 w-4)</option>
              <option value="h-5 w-5" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Normal (h-5 w-5)</option>
              <option value="h-6 w-6" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Grand (h-6 w-6)</option>
              <option value="h-7 w-7" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Très grand (h-7 w-7)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Section CTA */}
      <div>
        <h3 className="text-lg font-medium text-white mb-6 border-b border-white/10 pb-2">Bouton d'action (CTA)</h3>
        <div className="space-y-6">
          <div className="flex items-center space-x-3">
            <input type="checkbox" id="cta-enabled" checked={publicCTA.enabled} onChange={(e) => { const next = { ...publicCTA, enabled: e.target.checked }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
            <label htmlFor="cta-enabled" className="text-gray-200 cursor-pointer">Activer le bouton CTA</label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Texte du bouton
                <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.cta.label)</span>
              </label>
              <input type="text" value={publicCTA.label || ''} onChange={(e) => { const next = { ...publicCTA, label: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" placeholder="Ex: Nous contacter" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Lien
                <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.cta.href)</span>
              </label>
              <input type="text" value={publicCTA.href || ''} onChange={(e) => { const next = { ...publicCTA, href: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" placeholder="Ex: /contact" />
            </div>
          </div>
        </div>
        <div>
          <h4 className="text-md font-medium text-gray-200 mb-4">Logo et icône du CTA</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Position de l'icône
                <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.cta.iconPosition)</span>
              </label>
              <select value={publicCTA.iconPosition || 'left'} onChange={(e) => { const next = { ...publicCTA, iconPosition: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" style={{ colorScheme: 'dark' }}>
                <option value="left" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>À gauche</option>
                <option value="right" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>À droite</option>
                <option value="top" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Au-dessus</option>
                <option value="bottom" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>En-dessous</option>
              </select>
            </div>
          </div>
        </div>
        <div>
          <h4 className="text-md font-medium text-gray-200 mb-4">Style et taille du CTA</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Taille du texte
                <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.cta.fontSize)</span>
              </label>
              <select value={publicCTA.fontSize || 'text-sm'} onChange={(e) => { const next = { ...publicCTA, fontSize: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" style={{ colorScheme: 'dark' }}>
                <option value="text-xs" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Extra petit (xs)</option>
                <option value="text-sm" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Petit (sm)</option>
                <option value="text-base" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Normal (base)</option>
                <option value="text-lg" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Grand (lg)</option>
                <option value="text-xl" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Extra grand (xl)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Épaisseur du texte
                <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.cta.fontWeight)</span>
              </label>
              <select value={publicCTA.fontWeight || 'font-bold'} onChange={(e) => { const next = { ...publicCTA, fontWeight: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" style={{ colorScheme: 'dark' }}>
                <option value="font-light" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Léger (light)</option>
                <option value="font-normal" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Normal</option>
                <option value="font-medium" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Moyen (medium)</option>
                <option value="font-semibold" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Semi-gras (semibold)</option>
                <option value="font-bold" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Gras (bold)</option>
                <option value="font-extrabold" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Extra-gras (extrabold)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Padding
                <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.cta.padding)</span>
              </label>
              <select value={publicCTA.padding || 'px-6 py-3'} onChange={(e) => { const next = { ...publicCTA, padding: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" style={{ colorScheme: 'dark' }}>
                <option value="px-3 py-1.5" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Compact (3/1.5)</option>
                <option value="px-4 py-2" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Petit (4/2)</option>
                <option value="px-6 py-3" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Normal (6/3)</option>
                <option value="px-8 py-4" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Grand (8/4)</option>
                <option value="px-10 py-5" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Extra grand (10/5)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Bordure arrondie
                <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.cta.borderRadius)</span>
              </label>
              <select value={publicCTA.borderRadius || 'rounded-xl'} onChange={(e) => { const next = { ...publicCTA, borderRadius: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" style={{ colorScheme: 'dark' }}>
                <option value="rounded-none" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Aucune</option>
                <option value="rounded-sm" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Petite (sm)</option>
                <option value="rounded-md" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Moyenne (md)</option>
                <option value="rounded-lg" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Grande (lg)</option>
                <option value="rounded-xl" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Extra grande (xl)</option>
                <option value="rounded-full" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Complète (full)</option>
              </select>
            </div>
          </div>
        </div>
        <div>
          <h4 className="text-md font-medium text-gray-200 mb-4">Couleurs du CTA</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Couleur de fond
                <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.cta.backgroundColor)</span>
              </label>
              <div className="flex space-x-2">
                <input 
                  type="color" 
                  value={publicCTA.backgroundColor || '#e5347c'} 
                  onChange={(e) => { const next = { ...publicCTA, backgroundColor: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} 
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                />
                <input 
                  type="text" 
                  value={publicCTA.backgroundColor || '#e5347c'} 
                  onChange={(e) => { const next = { ...publicCTA, backgroundColor: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} 
                  className="w-24 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="#e5347c"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Couleur du texte
                <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.cta.textColor)</span>
              </label>
              <div className="flex space-x-2">
                <input 
                  type="color" 
                  value={publicCTA.textColor || '#ffffff'} 
                  onChange={(e) => { const next = { ...publicCTA, textColor: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} 
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                />
                <input 
                  type="text" 
                  value={publicCTA.textColor || '#ffffff'} 
                  onChange={(e) => { const next = { ...publicCTA, textColor: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} 
                  className="w-24 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="#ffffff"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Couleur de bordure
                <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.cta.borderColor)</span>
              </label>
              <div className="flex space-x-2">
                <input 
                  type="color" 
                  value={publicCTA.borderColor || '#1278ca'} 
                  onChange={(e) => { const next = { ...publicCTA, borderColor: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} 
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                />
                <input 
                  type="text" 
                  value={publicCTA.borderColor || '#1278ca'} 
                  onChange={(e) => { const next = { ...publicCTA, borderColor: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} 
                  className="w-24 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="#1278ca"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Couleur de l'icône
                <span className="text-gray-500 text-xs block">(tenant.config.publicHeader.cta.iconColor)</span>
              </label>
              <div className="flex space-x-2">
                <input 
                  type="color" 
                  value={publicCTA.iconColor || '#ffffff'} 
                  onChange={(e) => { const next = { ...publicCTA, iconColor: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} 
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                />
                <input 
                  type="text" 
                  value={publicCTA.iconColor || '#ffffff'} 
                  onChange={(e) => { const next = { ...publicCTA, iconColor: e.target.value }; handleConfigChange('publicHeader.cta', next); setPreviewValue('publicHeader.cta', next); }} 
                  className="w-24 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="#ffffff"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Menus Publics */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-white border-b border-white/10 pb-2">Menus publics</h3>
            <p className="text-gray-400 text-sm mt-2">Activez/désactivez, réorganisez, ajoutez ou supprimez les éléments du menu public</p>
            <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-blue-300 text-xs flex items-center gap-2">
                <span className="text-blue-400">💡</span>
                <span>
                  <strong>Services :</strong> Les sous-menus se mettent à jour automatiquement selon la configuration dans l'onglet Templates → Configuration des Services
                </span>
              </p>
            </div>
          </div>
          <button onClick={() => setShowAddForm(true)} className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors">
            <Plus className="h-4 w-4" />
            <span>Ajouter un menu</span>
          </button>
        </div>

        {showAddForm && (
          <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
            <h4 className="text-white font-medium mb-4">Ajouter un nouveau menu public</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nom du menu</label>
                <input type="text" value={newPublicMenuItem.name} onChange={(e) => setNewPublicMenuItem({ ...newPublicMenuItem, name: e.target.value })} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" placeholder="Ex: Tarifs" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">URL (optionnel)</label>
                <input type="text" value={newPublicMenuItem.href} onChange={(e) => setNewPublicMenuItem({ ...newPublicMenuItem, href: e.target.value })} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" placeholder="Ex: /tarifs" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Icône</label>
                <IconSelector
                  value={newPublicMenuItem.icon}
                  onChange={(value) => setNewPublicMenuItem({ ...newPublicMenuItem, icon: value })}
                  preset="all"
                />
              </div>
              <div className="flex items-center space-x-3">
                <input type="checkbox" id="new-public-menu-enabled" checked={newPublicMenuItem.enabled} onChange={(e) => setNewPublicMenuItem({ ...newPublicMenuItem, enabled: e.target.checked })} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
                <label htmlFor="new-public-menu-enabled" className="text-gray-200 cursor-pointer">Activé par défaut</label>
              </div>
            </div>
            <div className="flex space-x-3 mt-4">
              <button onClick={handlePublicAddMenuItem} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors">Ajouter</button>
              <button onClick={() => { setShowAddForm(false); setNewPublicMenuItem({ name: '', icon: 'Home', href: '', enabled: true }); }} className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md transition-colors">Annuler</button>
            </div>
          </div>
        )}

        <div className="space-y-2" style={{ minHeight: '20px' }}>
          {publicMenuItems.map((item: any) => (
            <div key={item.id} className="space-y-2">
              <div
                className={`flex items-center space-x-3 p-3 bg-white/5 rounded-lg border border-white/10 transition-all ${draggedItem === item.id ? 'shadow-lg bg-white/10 scale-105 opacity-50' : ''} ${dragOverItem === item.id ? 'border-green-400/50 bg-green-500/10' : ''}`}
                style={{ userSelect: 'none' }}
                draggable
                onDragStart={(e) => handlePublicDragStart(e, item.id)}
                onDragOver={(e) => handleDragOver(e, item.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handlePublicDrop(e, item.id)}
              >
                <div className="text-gray-400 hover:text-white cursor-grab active:cursor-grabbing flex-shrink-0" style={{ touchAction: 'none' }}>
                  <GripVertical className="h-5 w-5" />
                </div>
                <button onClick={() => handlePublicMenuItemToggle(item.id)} className={`p-1 rounded transition-colors ${item.enabled ? 'text-green-400 hover:text-green-300' : 'text-gray-500 hover:text-gray-400'}`}>
                  {item.enabled ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                </button>
                <div className="flex-1 flex items-center space-x-3">
                  {editingItem === item.id ? (
                    <div className="flex items-center space-x-2 flex-1">
                      <input 
                        type="text" 
                        value={editingValues[item.id]?.name || item.name} 
                        onChange={(e) => {
                          setEditingValues(prev => ({
                            ...prev,
                            [item.id]: { ...prev[item.id], name: e.target.value }
                          }));
                        }}
                        onKeyDown={(e) => { 
                          if (e.key === 'Enter') {
                            handlePublicEditMenuItem(item.id, editingValues[item.id] || { name: item.name, href: item.href, icon: item.icon });
                            setEditingItem(null);
                            setEditingValues({});
                          }
                          if (e.key === 'Escape') {
                            setEditingItem(null);
                            setEditingValues({});
                          }
                        }} 
                        className="px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-gray-400" 
                        autoFocus 
                      />
                      <IconSelector
                        value={editingValues[item.id]?.icon || item.icon}
                        onChange={(value) => {
                          setEditingValues({
                            ...editingValues,
                            [item.id]: {
                              ...editingValues[item.id],
                              icon: value
                            }
                          });
                        }}
                        preset="all"
                      />
                      <input 
                        type="text" 
                        value={editingValues[item.id]?.href || item.href} 
                        onChange={(e) => {
                          setEditingValues({
                            ...editingValues,
                            [item.id]: {
                              ...editingValues[item.id],
                              href: e.target.value
                            }
                          });
                          setEditingValues(prev => ({
                            ...prev,
                            [item.id]: { ...prev[item.id], href: e.target.value }
                          }));
                        }}
                        onKeyDown={(e) => { 
                          if (e.key === 'Enter') {
                            handlePublicEditMenuItem(item.id, editingValues[item.id] || { name: item.name, href: item.href, icon: item.icon });
                            setEditingItem(null);
                            setEditingValues({});
                          }
                          if (e.key === 'Escape') {
                            setEditingItem(null);
                            setEditingValues({});
                          }
                        }} 
                        className="px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 flex-1" 
                        placeholder="/chemin" 
                      />
                      <button 
                        onClick={() => {
                          const finalValues = editingValues[item.id] || { name: item.name, href: item.href, icon: item.icon };
                          handlePublicEditMenuItem(item.id, finalValues);
                          setEditingItem(null);
                          setEditingValues({});
                        }}
                        className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs transition-colors"
                        title="Valider"
                      >
                        ✓
                      </button>
                      <button 
                        onClick={() => {
                          setEditingItem(null);
                          setEditingValues({});
                        }}
                        className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs transition-colors"
                        title="Annuler"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className={`font-medium ${item.enabled ? 'text-white' : 'text-gray-500'}`}>{item.name}</span>
                      <span className="text-xs text-gray-500">({item.icon})</span>
                      <span className="text-xs text-gray-600">{item.href}</span>
                      {item.submenu && item.submenu.length > 0 && (
                        <span className="text-xs text-blue-400 bg-blue-500/20 px-2 py-1 rounded">
                          {item.submenu.length} sous-menu{item.submenu.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </>
                  )}
                </div>
                <div className="flex items-center space-x-1">
                  <button onClick={() => setEditingSubmenu(editingSubmenu === item.id ? null : item.id)} className="p-1 text-gray-400 hover:text-green-400 transition-colors" title="Gérer les sous-menus">
                    <Plus className="h-4 w-4" />
                  </button>
                  {/* Autoriser la modification pour tous (système et personnalisé) */}
                  <button
                    onClick={() => {
                      if (editingItem === item.id) {
                        setEditingItem(null);
                        setEditingValues({});
                      } else {
                        setEditingItem(item.id);
                        setEditingValues({ [item.id]: { name: item.name, href: item.href || '', icon: item.icon } });
                      }
                    }}
                    className="p-1 text-gray-400 hover:text-blue-400 transition-colors"
                    title="Modifier"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  {/* La suppression reste réservée aux éléments personnalisés */}
                  {item.isCustom ? (
                    <button onClick={() => handlePublicDeleteMenuItem(item.id)} className="p-1 text-gray-400 hover:text-red-400 transition-colors" title="Supprimer">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : (
                    <span className="text-xs text-gray-500 px-2 py-1 bg-blue-500/20 text-blue-400 rounded">Système</span>
                  )}
                </div>
              </div>

              {/* Édition des sous-menus */}
              {editingSubmenu === item.id && (
                <div className="ml-8 space-y-3 p-4 bg-white/3 rounded-lg border border-white/5">
                  <div className="flex items-center justify-between">
                    <h5 className="text-sm font-medium text-gray-300">Sous-menus de "{item.name}"</h5>
                    <button onClick={() => setEditingSubmenu(null)} className="p-1 text-gray-400 hover:text-white transition-colors">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input type="text" value={newSubMenuItem.name} onChange={(e) => setNewSubMenuItem({ ...newSubMenuItem, name: e.target.value })} placeholder="Nom du sous-menu" className="px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-gray-400" />
                    <IconSelector
                      value={newSubMenuItem.icon}
                      onChange={(value) => setNewSubMenuItem({ ...newSubMenuItem, icon: value })}
                      preset="all"
                    />
                    <input type="text" value={newSubMenuItem.href} onChange={(e) => setNewSubMenuItem({ ...newSubMenuItem, href: e.target.value })} placeholder="/sous-chemin" className="px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-gray-400" />
                    <button onClick={() => handleAddSubmenuItem(item.id)} className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors">Ajouter</button>
                  </div>

                  {item.submenu && item.submenu.length > 0 && (
                    <div className="space-y-2">
                      {item.submenu.map((subItem: any) => (
                        <div key={subItem.id} className={`flex items-center space-x-3 p-2 rounded border ${subItem.isAuto ? 'bg-blue-500/10 border-blue-500/30' : 'bg-white/5 border-white/10'}`}>
                          <button onClick={() => handleEditSubmenuItem(item.id, subItem.id, { enabled: !subItem.enabled })} className={`p-1 rounded transition-colors ${subItem.enabled ? 'text-green-400 hover:text-green-300' : 'text-gray-500 hover:text-gray-400'}`}>
                            {subItem.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                          </button>
                          <div className="flex-1 flex items-center space-x-2">
                            <span className={`text-sm ${subItem.enabled ? 'text-white' : 'text-gray-500'}`}>{subItem.name}</span>
                            <span className="text-xs text-gray-500">({subItem.icon})</span>
                            <span className="text-xs text-gray-600">{subItem.href}</span>
                            {subItem.isAuto && (
                              <span className="text-xs text-blue-400 bg-blue-500/20 px-2 py-0.5 rounded">
                                Auto
                              </span>
                            )}
                          </div>
                          {subItem.isCustom && (
                            <button onClick={() => handleDeleteSubmenuItem(item.id, subItem.id)} className="p-1 text-gray-400 hover:text-red-400 transition-colors" title="Supprimer">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                          {!subItem.isCustom && !subItem.isAuto && (
                            <span className="text-xs text-gray-500 px-2 py-1 bg-gray-500/20 text-gray-400 rounded">Système</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PublicHeaderConfig;
